// The scope selector: which workspace everything below is read through.
//
// IT SITS IN THE TOP BAR AND NOT IN THE SIDEBAR, because it is not a place you
// go. Every other thing in the sidebar answers "what am I looking at"; this
// answers "whose data", which is a different question and belongs beside who you
// are signed in as rather than in the list of destinations.
//
// "ALL WORKSPACES" IS FIRST AND IS THE DEFAULT. It is the only setting under
// which search can be global, so burying it under the named workspaces would hide
// the reason the control exists. It also stops the empty state being a lie: with
// one workspace selected, a table in another one is not "missing", it is out of
// scope, and you can only tell those apart if seeing everything is one click.

import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronsUpDown, Database, Layers, Loader2, Plus, Search, Users } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { StatusPill } from "@/components/ui/status";
import { bloxxData } from "@/lib/bloxxData";
import { useScope } from "@/lib/scope";
import { cn } from "@/lib/utils";

export function ScopeSelector() {
  const { scope, setScope, workspaces, current, isAll, loading } = useScope();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [newName, setNewName] = useState("");
  const nameRef = useRef<HTMLInputElement>(null);

  /**
   * TABLES SOMEBODY ELSE OWNS. They belong to no workspace of yours, so the
   * workspace list structurally cannot show them — which is why this is its own
   * call and why it lives HERE rather than in the Tables screen it used to sit
   * in. "Whose data am I looking at" is the question this control answers, and
   * half the answer being reachable only from one screen was the reason a shared
   * table was so easy to lose.
   */
  const sharedQ = useQuery({
    queryKey: ["shared"],
    queryFn: () => bloxxData.listShared(),
    enabled: open,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const shared = sharedQ.data?.tables ?? [];

  const create = useMutation({
    mutationFn: (name: string) => bloxxData.createWorkspace(name),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["data", "workspaces"] });
      // Land in the thing you just made. Creating a workspace and staying in
      // "all" is the one case where the scope should move without being asked.
      setScope(r.workspace.id);
    },
  });

  const hits = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return { own: workspaces, shared };
    return {
      own: workspaces.filter((w) => w.name.toLowerCase().includes(needle)),
      shared: shared.filter(
        (t) =>
          (t.name || t.slug).toLowerCase().includes(needle) ||
          (t.owner || "").toLowerCase().includes(needle),
      ),
    };
  }, [q, workspaces, shared]);

  // Grouped by WHO shared it. "A table called Guides" from nobody in particular
  // is not something anyone can act on.
  const owners = [...new Set(hits.shared.map((t) => t.owner))];

  const submitNew = () => {
    const name = newName.trim();
    if (!name) return;
    create.mutate(name);
    setNewName("");
    setOpen(false);
  };

  /**
   * NOTHING TO SCOPE, BUT SOMETHING TO MAKE. This used to return null, on the
   * grounds that a picker with one choice called "All" is furniture. It was also
   * the only place a workspace could be created, so a new account had no way to
   * make its first one, and Tables' empty state sent people here to a control
   * that was not drawn. With nothing to pick, it is just the create field.
   */
  if (!loading && workspaces.length === 0 && sharedQ.isSuccess && shared.length === 0) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex h-8 items-center gap-2 rounded-md border px-2.5 text-sm transition-colors hover:bg-secondary"
          >
            <Plus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            New workspace
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-[17rem] p-2">
          <p className="px-1 pb-2 text-xs text-muted-foreground">
            Tables and Studio recordings live in a workspace. You can make more later.
          </p>
          <div className="flex gap-1">
            <Input
              autoFocus
              className="h-8 text-sm"
              placeholder="Workspace name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitNew()}
            />
            <Button size="sm" className="h-8 shrink-0 px-2" disabled={!newName.trim() || create.isPending} onClick={submitNew}>
              {create.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQ("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Choose which workspace you are looking at"
          className="flex h-8 max-w-[15rem] items-center gap-2 rounded-md border px-2.5 text-sm transition-colors hover:bg-secondary"
        >
          {isAll ? (
            <Layers className="h-3.5 w-3.5 shrink-0 text-accent" />
          ) : (
            <Database className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          )}
          <span className="min-w-0 truncate">
            {loading ? "Workspaces…" : isAll ? "All workspaces" : current?.name}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-[17rem] p-0">
        {/* Search only appears once a list is long enough to need one. Four
            workspaces and a search box is a search box in the way. */}
        {workspaces.length > 6 && (
          <div className="border-b p-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                autoFocus
                className="h-8 pl-7 text-sm"
                placeholder="Find a workspace"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
          </div>
        )}

        <div className="max-h-72 overflow-auto p-1">
          {sharedQ.isPending && <p role="status" className="px-2 py-2 text-xs text-muted-foreground">Loading shared tables…</p>}
          {sharedQ.isError && <button className="px-2 py-2 text-xs underline" onClick={() => void sharedQ.refetch()}>Shared tables could not load. Try again</button>}
          <button
            onClick={() => {
              setScope(null);
              setOpen(false);
            }}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
          >
            <Layers className="h-3.5 w-3.5 shrink-0 text-accent" />
            <span className="min-w-0 flex-1 truncate">All workspaces</span>
            <Check className={cn("h-3.5 w-3.5 shrink-0", isAll ? "opacity-100" : "opacity-0")} />
          </button>

          {/* Said out loud, because "why can I suddenly find things I could not
              find a minute ago" is otherwise a mystery. */}
          <p className="px-2 pb-1.5 pl-9 text-[11px] leading-snug text-muted-foreground">
            Search covers everything you have.
          </p>

          <div className="my-1 border-t" />

          {hits.own.map((w) => (
            <button
              key={w.id}
              onClick={() => {
                setScope(w.id);
                setOpen(false);
              }}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
            >
              <Database className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate">{w.name}</span>
              {w.role === "reader" && <StatusPill tone="neutral">read</StatusPill>}
              <Check className={cn("h-3.5 w-3.5 shrink-0", w.id === scope ? "opacity-100" : "opacity-0")} />
            </button>
          ))}

          {/* A shared table is not a scope — it is one table, so picking it OPENS
              it rather than narrowing everything else to it. Said plainly by the
              heading rather than by the two behaving identically and one of them
              surprising you. */}
          {owners.map((owner) => (
            <div key={owner} className="mt-1">
              <p className="flex items-center gap-1.5 px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                <Users className="h-3 w-3" />
                {owner || "Shared with you"}
              </p>
              {hits.shared
                .filter((t) => t.owner === owner)
                .map((t) => (
                  <button
                    key={t.table_id}
                    onClick={() => {
                      setOpen(false);
                      navigate(`/tables/${t.workspace_id}/${t.table_id}`);
                    }}
                    className="flex w-full items-center gap-2 rounded px-2 py-1.5 pl-6 text-left text-sm hover:bg-muted"
                  >
                    <span className="min-w-0 flex-1 truncate">{t.name || t.slug}</span>
                    {t.role === "reader" && <StatusPill tone="neutral">read</StatusPill>}
                  </button>
                ))}
            </div>
          ))}

          {q && hits.own.length === 0 && hits.shared.length === 0 && (
            <p className="px-2 py-3 text-center text-xs text-muted-foreground">Nothing matches “{q}”.</p>
          )}
        </div>

        <div className="border-t p-2">
          <div className="flex gap-1">
            <Input
              ref={nameRef}
              className="h-8 text-sm"
              placeholder="New workspace"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitNew()}
            />
            <Button
              size="sm"
              className="h-8 shrink-0 px-2"
              disabled={!newName.trim() || create.isPending}
              onClick={submitNew}
            >
              {create.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
